import React, { useState } from 'react'
import { View, StyleSheet } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import moment from 'moment'

import { CustomText } from '../../text/StyledText'
import { RoundSquareBtn, RoundSquareDeleteBtn } from '../../buttons/StyledButtons'
import SurveyPromptDialog, { platforms, shouldShowSurveyPrompt } from '../../dialogs/SurveyPromptDialog'

import { getConversationId } from '../../../services/api/Inbox'
import { cancelBooking } from '../../../services/api/snakSammen'
import startVideoSession from '../../../utils/startVideoSession'
import { surveyIds } from '../../../services/api/Surveys'
import { txt } from '../../../locales/i18n'

import Layout from '../../../constants/Layout'
import Colors from '../../../constants/Colors'
import { CornerStyles, ShadowStyles } from '../../../styles'
import { CancelWithMessageDialog, CannotCancelDialog } from './dialogs/CancelDialogs'

export function BookedSessions(props) {
  const [showSurveyPrompt, setShowSurveyPrompt] = useState(false)
  const [cannotCancelDialogVisible, setCannotCancelDialogVisible] = useState(false)
  const [cancelMessageDialogVisible, setCancelMessageDialogVisible] = useState(false)
  const navigation = useNavigation()

  async function handleSendMessage(receiverId, vsSessionId) {
    if (receiverId === undefined || vsSessionId === undefined) return
    const conversationId = await getConversationId({ vsSessionId, receiverId })
    const params = { id: conversationId ?? 'new' }

    if (conversationId === null) {
      params.receiverId = receiverId
      params.vsSessionId = vsSessionId
    }

    const options = { screen: 'Conversation', initial: false, params }
    navigation.navigate('InboxStack', options)
  }

  async function handleStartSession(roomKey, vonageSessionId) {
    await startVideoSession(roomKey, vonageSessionId, 'snaksammen')
    // const showSurveyPrompt = await shouldShowSurveyPrompt(surveyIds.snaksammenUser)
    // setShowSurveyPrompt(showSurveyPrompt)
  }

  async function handleCancelBooking(session) {
    const now = new Date()
    const startDate = new Date(session.start_date)
    const tenMinutesBeforeStartDate = new Date(new Date(session.start_date).setMinutes(startDate.getMinutes() - 10))

    if (now >= startDate && now >= tenMinutesBeforeStartDate) {
      setCannotCancelDialogVisible(true)
    } else if (now < startDate && now > tenMinutesBeforeStartDate) {
      setCancelMessageDialogVisible(true)
    } else {
      await cancelBooking(session.id)
      props.handleRefresh()
    }
  }

  async function handleCancel(session) {
    await cancelBooking(session.id)
    props.handleRefresh()
    setCancelMessageDialogVisible(false)
  }

  async function handleSendMessageAndCancel(session) {
    await handleCancel(session)
    await handleSendMessage(session.volunteer_id, session.id)
  }

  async function handleCancelMessage(session) {
    setCannotCancelDialogVisible(false)
    await handleSendMessage(session.volunteer_id, session.id)
  }

  return (
    <View style={styles.container}>
      <View>
        <CustomText font="bold" style={styles.titleBold} accessibilityRole="header" aria-level={2}>
          {txt('videoChat.bookedSessions')}
        </CustomText>
        {props.bookedSessions.length === 0 && (
          <CustomText style={styles.noSession}>{txt('videoChat.noBookedSessions')}</CustomText>
        )}
      </View>

      {props.bookedSessions.map((session) => {
        const date = moment(session.start_date).format('dddd [d.] DD/MM [kl.] HH:mm')
        const dateString = date.charAt(0).toUpperCase() + date.slice(1)

        const duration = (new Date(session.end_date).getTime() - new Date(session.start_date).getTime()) / 1000 / 60
        const durationString = `${txt('videoChat.duration')}: ${duration} min.`

        return (
          <View key={session.id} style={styles.dropdownContainer}>
            <View style={styles.flex2}>
              <CustomText font="small">{dateString}</CustomText>

              <CustomText font="small" text={durationString} />

              <CustomText font="small" text={txt('videoChat.volunteer') + ': ' + session.username} />
            </View>

            <View style={styles.buttonsContainer}>
              <View style={styles.buttonStartContainer}>
                <RoundSquareBtn
                  title={txt('videoChat.start')}
                  onPress={() => handleStartSession(session.room_key, session.vonage_session_id)}
                  style={styles.button}
                />
              </View>

              <View style={styles.buttonConversationContainer}>
                <RoundSquareBtn
                  title={txt('conversations.messageSend')}
                  onPress={() => handleSendMessage(session.volunteer_id, session.id)}
                  style={styles.button}
                />
              </View>

              <View style={styles.buttonCancelContainer}>
                <RoundSquareDeleteBtn
                  title={txt('videoChat.cancel')}
                  style={styles.button}
                  onPress={() => handleCancelBooking(session)}
                />
              </View>
            </View>
            <CancelWithMessageDialog
              visible={cancelMessageDialogVisible}
              onDismiss={async () => await handleCancel(session)}
              sendMessage={() => handleSendMessageAndCancel(session)}
            />
            <CannotCancelDialog
              visible={cannotCancelDialogVisible}
              onDismiss={() => setCannotCancelDialogVisible(false)}
              sendMessage={() => handleCancelMessage(session)}
            />
          </View>
        )
      })}

      <SurveyPromptDialog
        visible={showSurveyPrompt}
        surveyId={surveyIds.snaksammenUser}
        platform={platforms.SnakSammen}
        surveyLink={'https://www.survey-xact.dk/LinkCollector?key=JLPMT31WUK36'}
        onDismiss={() => setShowSurveyPrompt(false)}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  buttonCancelContainer: {
    flex: 1,
    justifyContent: 'center',
  },

  buttonConversationContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingBottom: Layout.isSmallDevice ? 10 : 0,
    paddingRight: 2,
  },

  buttonStartContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingBottom: Layout.isSmallDevice ? 10 : 0,
    paddingRight: 2,
  },

  buttonsContainer: {
    flex: Layout.isSmallDevice ? 1 : 2,
    flexDirection: Layout.isSmallDevice ? 'column' : 'row',
    justifyContent: 'space-evenly',
  },

  button: {
    paddingHorizontal: 0,
  },

  container: {
    backgroundColor: Colors.background,
    margin: 10,
  },

  dropdownContainer: {
    backgroundColor: Colors.white,
    ...CornerStyles.borderRadiusS,
    marginTop: 10,
    padding: 10,
    ...ShadowStyles.shadowInputs,
    flexDirection: 'row',
  },

  flex2: {
    flex: 2,
    justifyContent: 'center',
  },

  noSession: {
    color: Colors.inactive,
    marginTop: 10,
    textAlign: 'center',
  },

  titleBold: {
    textAlign: 'center',
  },
})
